// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { TenantConfig } from "../core/models/types";
import { DIRECTOR_DASHBOARD_PAGES, INSIGHTS_PAGES } from "../core/views";
import * as dashboard from "../RootStore/TenantStore/dashboardTenants";
import { usMiFilterByUserDistrict } from "../WorkflowsStore/utils";

const US_MI_CONFIG = {
  name: "Michigan",
  stateCode: "MI",
  domain: "michigan.gov",
  availableStateCodes: [dashboard.US_MI],
  enableUserRestrictions: false,
  sessionTimeoutMinutes: 15,
  workflowsSupportedSystems: ["SUPERVISION", "INCARCERATION"],
  workflowsMethodologyUrl:
    "https://drive.google.com/file/d/1tdYaic6jvsdTZHZTeGzUVtHL7_SGfyk5/view",
  workflowsSystemConfigs: {
    SUPERVISION: {
      search: [
        {
          searchType: "OFFICER",
          searchField: ["officerId"],
          searchTitle: "agent",
        },
      ],
      staffFilterFn: usMiFilterByUserDistrict,
    },
    INCARCERATION: {
      search: [
        {
          searchType: "FACILITY",
          searchField: ["facilityId"],
          searchTitle: "facility",
          restrictedToFeatureVariant: "usMiFacilitySearch",
        },
        {
          searchType: "INCARCERATION_OFFICER",
          searchField: ["officerId"],
          searchTitle: "case manager",
          restrictedToFeatureVariant: "usMiCaseManagerSearch",
        },
      ],
    },
  },
  insightsLanternState: true,
  navigation: {
    insights: [INSIGHTS_PAGES.supervision],
    directorDashboard: [DIRECTOR_DASHBOARD_PAGES.supervision],
    workflows: ["home", "clients", "residents"],
  },
  featureVariants: {
    outcomesModule: {},
    supervisorHomepageReviewCard: {},
    insightsHideOfficersWithoutEligibleCaseload: {},
  },
  custodyLevelCopy: {
    MINIMUM: "Security Level I",
    RESTRICTIVE_MINIMUM: "Security Level II",
    CLOSE: "Security Level IV",
    MAXIMUM: "Security Level V",
    INTERNAL_UNKNOWN: "Unknown Security Level",
    EXTERNAL_UNKNOWN: "Unknown Security Level",
    null: "Unknown Security Level",
  },
  directorDashboard: {
    navTitle: "Regional Dashboard",
    lookerDashboard: "michigan_region_metrics",
  },
} satisfies TenantConfig<"US_MI">;

export default US_MI_CONFIG;
